// Shapes the raw Leaves/LeaveBalances sheet rows (snake_case, straight off
// sheetsApi.js) into the camelCase view the Leaves screen renders, and joins
// in each request's employee name/role from Users. Kept separate from
// sheetsApi.js since that file is a thin, business-logic-free sheet reader —
// the join/derivation below is specific to how the Leaves page displays this.

export const LEAVE_TYPES = ['Sick Leave', 'Vacation Leave'];

// LeaveBalances stores credits_total (an admin-set yearly maximum grant)
// and credits_remaining (the live current balance) directly — "used" is the
// only derived value here, computed as total - remaining. That's simpler
// and more correct than summing approved-leave days now that an admin can
// adjust `remaining` directly (a manual correction, unrelated to any
// approval, would otherwise drift out of sync with a sum-of-approvals).
export function buildLeaveViewData(users, leaves, leaveBalances) {
  const userByUuid = new Map(users.map((u) => [u.uuid, u]));

  const decoratedLeaves = leaves.map((l) => {
    const user = userByUuid.get(l.uuid);
    return {
      leaveId: l.leave_id,
      uuid: l.uuid,
      employee: user ? user.name : l.uuid,
      role: user ? user.role : '',
      isAdmin: user ? user.isAdmin : false,
      leaveType: l.leave_type,
      dateFrom: l.date_from,
      dateTo: l.date_to,
      days: Number(l.days) || 0,
      halfDayPeriod: l.half_day_period || '',
      reason: l.reason || '',
      status: l.status,
      filedAt: l.filed_at,
      decidedBy: l.decided_by,
      decidedAt: l.decided_at,
      decisionNotes: l.decision_notes || '',
    };
  });

  const balanceByUuid = new Map(
    leaveBalances.map((b) => [b.uuid, { total: Number(b.credits_total) || 0, remaining: Number(b.credits_remaining) || 0 }])
  );

  const balances = {};
  users.forEach((u) => {
    const { total, remaining } = balanceByUuid.get(u.uuid) || { total: 0, remaining: 0 };
    balances[u.uuid] = { remaining, used: total - remaining, total };
  });

  return { leaves: decoratedLeaves, balances };
}

// How many requests are awaiting an admin's decision right now — used for
// the Sidebar badge and the Admin tab badge. Operates on the raw sheet rows
// (just needs `status`), no join required.
export function countPendingLeaves(leaves) {
  return leaves.filter((l) => l.status === 'Pending').length;
}
